import { Column, Heading } from '@stenajs-webui/core';
import { ActionMenuItem, ActionMenuSeparator, stenaPrint } from '@stenajs-webui/elements';
import { ActionMenuSecondaryButton } from '@stenajs-webui/panels';
import { cssColor } from '@stenajs-webui/theme';
import { lazy, Suspense } from 'react';
import { isIntermodalBookingClaimable } from '../../../common/claims/claimsUtils';
import { stringifyIntermodalBooking } from '../../../common/formatters/bookingStringFormatter';
import { useLocalDateFormat } from '../../../common/hooks/useLocalDateFormat';
import { type TrackerCategory, trackEvent } from '../../../common/tracking/trackerService';
import { IntermodalBookingStatusTag } from '../../../common-ui/BookingStatusTag';
import { CopyActionMenuItem } from '../../../common-ui/CopyActionMenuItem';
import { ModalHeaderContainer } from '../../../common-ui/modal/ModalHeaderContainer';
import type { DetailedIntermodalBookingFragment } from '../../../gql/graphql';
import {
  ACTION_MENU_PLACEMENT,
  ACTION_MENU_WIDTH,
} from '../../manage/details/components/BookingActionMenuButton';
import { ActionMenuClaimsLink } from '../../manage/grid/components/options-table-cell/ActionMenuClaimsLink';

const IntermodalPrintButton = lazy(() => import('../../../common/pdf/IntermodalPrintButton'));

interface IntermodalBookingUpdateHeaderProps {
  booking: DetailedIntermodalBookingFragment;
  className: string;
  onClose: () => void;
}

const trackerCategory: TrackerCategory = 'IntermodalUpdate';

export const IntermodalBookingUpdateHeader = ({
  className,
  booking,
  onClose,
}: IntermodalBookingUpdateHeaderProps) => {
  const dateFormat = useLocalDateFormat();
  const showClaimButton = isIntermodalBookingClaimable(booking.status.status);

  const onClickCopy = () => trackEvent({ category: trackerCategory, action: 'Copy to clipboard' });

  const onClickClaims = () => {
    trackEvent({ category: trackerCategory, action: 'Make claim' });
  };

  return (
    <ModalHeaderContainer className={className} onRequestClose={onClose} aria-label={'Close'}>
      <Heading variant={'h3'} color={cssColor('--lhds-color-ui-900')}>
        {booking.bookingNo}
      </Heading>
      <IntermodalBookingStatusTag status={booking.status} />
      <Column width={'100%'} alignItems={['stretch', null, 'flex-end']}>
        <ActionMenuSecondaryButton
          placement={ACTION_MENU_PLACEMENT}
          label={'Booking actions'}
          menuWidth={ACTION_MENU_WIDTH}
          renderItems={close => (
            <>
              <Suspense
                fallback={<ActionMenuItem label={'Print booking'} leftIcon={stenaPrint} loading />}
              >
                <IntermodalPrintButton booking={booking} dateFormat={dateFormat} />
              </Suspense>
              <CopyActionMenuItem
                getValue={() => stringifyIntermodalBooking(booking, dateFormat)}
                onClick={onClickCopy}
              />
              {showClaimButton && (
                <>
                  <ActionMenuSeparator />
                  <ActionMenuClaimsLink
                    bookingNo={booking.bookingNo}
                    onClick={() => {
                      close();
                      onClickClaims();
                    }}
                  />
                </>
              )}
            </>
          )}
        />
      </Column>
    </ModalHeaderContainer>
  );
};
